import { Button } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { handoffUrl, isSafeNextPath, readHandoffCode } from "@how/tenancy";
import { LoadingState } from "@how/ui";
import type { AppEnv } from "./env";
import { useSupabase } from "./session";
import { primaryVars, secondaryVars } from "./sign-in/buttons";
import "./sign-in/sign-in.css";
import type { AppSupabaseClient } from "./supabase";

export type HandoffFailure = "missing" | "gone" | "failed";
export type RedeemAnswer =
  | {
      readonly ok: true;
      readonly next: string;
      readonly tokenHash: string | null;
      /** The person's address, masked by HoW (`j***@example.com`). */
      readonly email: string;
    }
  | { readonly ok: false; readonly reason: Exclude<HandoffFailure, "missing"> };

/** A spent code waiting for the person's click (ruling H8): the token hash lives only here, in memory. */
export interface PendingHandoff {
  readonly email: string;
  readonly next: string;
  readonly tokenHash: string;
  /** Someone else is signed in on this site; continuing signs them out here first. */
  readonly replacesSession: boolean;
}
export type ContinueOutcome = "done" | HandoffFailure | { readonly confirm: PendingHandoff };

export interface ContinueDeps {
  readonly hash: string;
  clearHash(): void;
  currentSession(): Promise<{ accessToken: string; userId: string } | null>;
  redeem(code: string, accessToken: string | null): Promise<RedeemAnswer>;
  verify(tokenHash: string): Promise<void>;
  signOutLocal(): Promise<void>;
  go(path: string): void;
}

/**
 * Asks HoW for a code for the target and leaves this site for its continue page (spec section 6). The host must come
 * from server-resolved data (a church's own domain rows), never from anything a person typed.
 */
export async function startHandoff(
  supabase: AppSupabaseClient,
  targetHost: string,
  nextPath = "/",
): Promise<void> {
  const { data, error } = await supabase.rpc("issue_handoff", {
    p_target_host: targetHost,
    p_next_path: nextPath,
  });
  if (error) throw error;
  if (typeof data !== "string" || data === "")
    throw new Error("HoW did not issue a hand-off code.");
  // A full page load: nothing of this church stays in memory when the person lands on another site.
  window.location.assign(handoffUrl(targetHost, data));
}

export function useHandoff() {
  const supabase = useSupabase();
  const m = useMutation({
    mutationFn: ({ host, path }: { host: string; path: string }) =>
      startHandoff(supabase, host, path),
  });
  return {
    open: (host: string, path = "/") => {
      if (m.isPending) return;
      m.mutate({ host, path });
    },
    isPending: m.isPending,
    error: m.error as unknown,
  };
}

/**
 * The target's side: spend the code, then either keep the session already here (the same person, R4) and open the
 * next page, or hand back a pending hand-off for the person to confirm. Nobody is signed in or switched without a
 * click (ruling H8): a planted link must not silently sign someone into another account.
 */
export async function continueHandoff(d: ContinueDeps): Promise<ContinueOutcome> {
  const code = readHandoffCode(d.hash);
  // Before any network call: the code must not stay in history, nor be shared with the address bar.
  d.clearHash();
  if (code === null) return "missing";
  let current: Awaited<ReturnType<ContinueDeps["currentSession"]>>;
  try {
    current = await d.currentSession();
  } catch {
    return "failed";
  }
  const answer = await d
    .redeem(code, current?.accessToken ?? null)
    .catch((): RedeemAnswer => ({ ok: false, reason: "failed" }));
  if (!answer.ok) return answer.reason;
  // `next` is never decoded; anything that is not a plain same-site path goes nowhere.
  if (!isSafeNextPath(answer.next)) return "failed";
  if (answer.tokenHash === null) {
    d.go(answer.next);
    return "done";
  }
  return {
    confirm: {
      email: answer.email,
      next: answer.next,
      tokenHash: answer.tokenHash,
      replacesSession: current !== null,
    },
  };
}

/** The person clicked Continue: sign whoever is here out of this site, take the new session, open the next page. */
export async function finishHandoff(
  d: Pick<ContinueDeps, "verify" | "signOutLocal" | "go">,
  pending: PendingHandoff,
): Promise<"done" | "failed"> {
  try {
    if (pending.replacesSession) await d.signOutLocal();
    await d.verify(pending.tokenHash);
  } catch {
    return "failed";
  }
  d.go(pending.next);
  return "done";
}

export const HANDOFF_FAILURE_MESSAGE =
  "This link has expired or was already used. Sign in to continue.";

function continueDeps(supabase: AppSupabaseClient, env: AppEnv): ContinueDeps {
  return {
    hash: window.location.hash,
    clearHash: () => {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    },
    currentSession: async () => {
      const { data } = await supabase.auth.getSession();
      const s = data.session;
      return s === null ? null : { accessToken: s.access_token, userId: s.user.id };
    },
    redeem: async (code, accessToken) => {
      const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/handoff-redeem`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${accessToken ?? env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ code }),
      });
      // Every refusal is one status and one message; a 500 has another body shape. No per-status mapping.
      if (!res.ok) return { ok: false, reason: res.status === 410 ? "gone" : "failed" };
      const body = (await res.json()) as { next?: unknown; tokenHash?: unknown; email?: unknown };
      if (typeof body.next !== "string") return { ok: false, reason: "failed" };
      const tokenHash = typeof body.tokenHash === "string" ? body.tokenHash : null;
      const email = typeof body.email === "string" ? body.email : "";
      // A session to take needs a name to confirm it by.
      if (tokenHash !== null && email === "") return { ok: false, reason: "failed" };
      return { ok: true, next: body.next, tokenHash, email };
    },
    verify: async (tokenHash) => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: "magiclink",
      });
      if (error) throw error;
    },
    signOutLocal: async () => {
      await supabase.auth.signOut({ scope: "local" });
    },
    go: (path) => {
      window.location.replace(path);
    },
  };
}

type PageState =
  | { readonly phase: "working" }
  | { readonly phase: "confirm"; readonly pending: PendingHandoff; readonly busy: boolean }
  | { readonly phase: "failed" };

/** The sign-in panel's dark surface, one column: the hand-off's own pages. */
function HandoffPanel({ children }: { children: ReactNode }) {
  return (
    <main className="how-signin how-signin--single">
      <div className="how-signin-form-panel">
        <div className="how-signin-form">{children}</div>
      </div>
    </main>
  );
}

/** /auth/continue on every app: no session needed; the Shell and its guards run after the full load. */
export function HandoffContinuePage({ env }: { env: AppEnv }) {
  const supabase = useSupabase();
  const deps = useMemo(() => continueDeps(supabase, env), [supabase, env]);
  const [state, setState] = useState<PageState>({ phase: "working" });
  const failedHeading = useRef<HTMLHeadingElement>(null);
  // The code is spent by the first attempt; StrictMode's second effect run must not read the cleared fragment.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void continueHandoff(deps)
      .then((result) => {
        if (result === "done") return;
        setState(
          typeof result === "string"
            ? { phase: "failed" }
            : { phase: "confirm", pending: result.confirm, busy: false },
        );
      })
      .catch(() => {
        setState({ phase: "failed" });
      });
  }, [deps]);
  useEffect(() => {
    // The Continue button disables while it works; when it fails, focus goes to what replaced it.
    if (state.phase === "failed") failedHeading.current?.focus();
  }, [state.phase]);

  if (state.phase === "working") return <LoadingState label="Opening" />;
  if (state.phase === "confirm") {
    const { pending, busy } = state;
    return (
      <HandoffPanel>
        <h1 className="how-signin-heading">Continue?</h1>
        <p className="how-signin-sub">
          This link signs you in as <strong>{pending.email}</strong>. Continue only if that is you.
          {pending.replacesSession
            ? " You'll be signed out of the account you're using on this site."
            : null}
        </p>
        <div className="how-signin-fields">
          <Button
            type="button"
            fullWidth
            loading={busy}
            className="how-signin-primary"
            vars={primaryVars}
            onClick={() => {
              setState({ phase: "confirm", pending, busy: true });
              void finishHandoff(deps, pending).then((result) => {
                if (result !== "done") setState({ phase: "failed" });
              });
            }}
          >
            Continue as {pending.email}
          </Button>
        </div>
        <Button
          component="a"
          href="/sign-in"
          fullWidth
          className="how-signin-secondary"
          vars={secondaryVars}
          aria-disabled={busy}
          onClick={(e) => {
            if (busy) e.preventDefault();
          }}
        >
          Not you? Cancel
        </Button>
      </HandoffPanel>
    );
  }
  return (
    <HandoffPanel>
      <h1 className="how-signin-heading" ref={failedHeading} tabIndex={-1}>
        This link can't be used
      </h1>
      <p className="how-signin-sub">{HANDOFF_FAILURE_MESSAGE}</p>
      <div className="how-signin-fields">
        <Button
          component="a"
          href="/sign-in"
          fullWidth
          className="how-signin-primary"
          vars={primaryVars}
        >
          Sign in
        </Button>
      </div>
    </HandoffPanel>
  );
}
